"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useBreakpoint } from "@/hooks/useBreakpoint";

const digitsOnly = (s = "") => s.replace(/[^\d]/g, "");

const DEFAULT_WHATSAPP_MESSAGE =
  "Hi ICIEOS team! I’d like to learn more about your services.";
const DEFAULT_EMAIL_SUBJECT = "Inquiry from ICIEOS Portfolio Website";
const DEFAULT_EMAIL_BODY =
  "Hi ICIEOS team! I’d like to learn more about your services.";

const buildGmailComposeUrl = (email, subject = "", body = "") => {
  const mailto = `mailto:${email}${
    subject || body
      ? `?${[
          subject ? `subject=${encodeURIComponent(subject)}` : "",
          body ? `body=${encodeURIComponent(body)}` : "",
        ]
          .filter(Boolean)
          .join("&")}`
      : ""
  }`;
  return `https://mail.google.com/mail/?extsrc=mailto&url=${encodeURIComponent(mailto)}`;
};

const buildOutlookComposeUrl = (email, subject = "", body = "") => {
  return `https://outlook.live.com/mail/deeplink/compose?to=${encodeURIComponent(
    email
  )}${subject ? `&subject=${encodeURIComponent(subject)}` : ""}${
    body ? `&body=${encodeURIComponent(body)}` : ""
  }`;
};

const buildMailto = (email, subject = "", body = "") => {
  const q = [
    subject ? `subject=${encodeURIComponent(subject)}` : "",
    body ? `body=${encodeURIComponent(body)}` : "",
  ]
    .filter(Boolean)
    .join("&");
  return `mailto:${email}${q ? `?${q}` : ""}`;
};

function IconBadgeImg({ src, alt }) {
  // Lazy-load Image component only where it's used
  const Image = require("next/image").default;
  return (
    <div
      className="relative h-10 w-10 sm:h-[45px] sm:w-[45px] md:h-[50px] md:w-[50px] lg:h-[55px] lg:w-[55px] xl:h-14 xl:w-14 laptop:h-[55px] laptop:w-[55px] 
      3xl:h-[70px] 3xl:w-[70px] 4xl:h-20 4xl:w-20  flex items-center justify-center"
    >
      <span className="absolute inset-1 -z-1 rounded-[14px] bg-[linear-gradient(55.42deg,rgba(93,110,243,0.6)_2.27%,rgba(255,28,247,0.6)_99.23%)] blur-[15px] sm:blur-[18px] md:blur-[20px] lg:blur-6 laptop:blur-6 3xl:blur-[38px] 4xl:blur-[48px]" />
      <Image
        src={src}
        alt={alt}
        width={96}
        height={96}
        sizes="(max-width: 639px) 40px,
               (max-width: 767px) 44px,
               (max-width: 1023px) 48px,
               (max-width: 1535px) 48px,
               (max-width: 1919px) 52px,
               (max-width: 2559px) 52px,
               70px"
        className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 3xl:h-[52px] 3xl:w-[52px] 4xl:h-[70px] 4xl:w-[70px] object-contain"
      />
    </div>
  );
}

function EmailPopupModal({ open, onClose, onSelect }) {
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef(null);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (typeof window === "undefined" || !mounted || !open) return null;

  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  return createPortal(
    <div
      onMouseDown={handleOverlayClick}
      className="fixed inset-0 bg-black/80 backdrop-blur-xs flex items-center justify-center z-50"
    >
      <div
        ref={modalRef}
        className="relative bg-[#ffffff] dark:bg-[#181a20] border border-[#ff1cf7] dark:border-[#df27df] p-6 rounded-xl flex flex-col items-center gap-4 w-[300px]"
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-[#6b7280] dark:text-[#b0b3b8] text-[20px cursor-pointer"
        >
          ×
        </button>
        <h3 className="font-inherit text-lg font-semibold text-center mt-3">
          Choose your email service
        </h3>
        <div className="flex gap-3">
          <button
            onClick={() => onSelect("gmail")}
            className="bg-[#DF27DF] text-white px-4 py-2 rounded-lg hover:opacity-90 transition cursor-pointer"
          >
            Gmail
          </button>
          <button
            onClick={() => onSelect("outlook")}
            className="bg-white text-[#DF27DF] px-4 py-2 rounded-lg hover:opacity-90 transition cursor-pointer"
          >
            Outlook
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Connect({
  className = "",
  heading = "CONNECT WITH US DIRECTLY",
  blurb = "For immediate assistance or inquiries, reach out to us through any of the channels below",

  isDark,
  whatsappMessage = DEFAULT_WHATSAPP_MESSAGE,
  emailSubject = DEFAULT_EMAIL_SUBJECT,
  emailBody = DEFAULT_EMAIL_BODY,
  ButtonComponent,
  handleScroll,
}) {
  const bp = useBreakpoint();
  const isMobileBP = bp === "default" || bp === "mobile" || bp === "sm";
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");

  if (!ButtonComponent) return null; // Safety check
  const contacts = [
    {
      label: "Email",
      value: process.env.NEXT_PUBLIC_CONTACT_EMAIL,
      icon: "/assets/contact/letter.svg",
    },
    {
      label: "Phone",
      value: process.env.NEXT_PUBLIC_CONTACT_PHONE,
      icon: "/assets/contact/call.svg",
    },
    {
      label: "Whatsapp",
      value: process.env.NEXT_PUBLIC_CONTACT_WHATSAPP,
      icon: "/assets/contact/whatsapp.svg",
    },
  ];

  const getContactHref = (label, value) => {
    const key = String(label || "").toLowerCase();
    if (key.includes("email")) {
      return isMobileBP
        ? buildMailto(value, emailSubject, emailBody)
        : buildGmailComposeUrl(value, emailSubject, emailBody);
    }
    if (key.includes("phone") || key.includes("call")) {
      return `tel:${String(value).replace(/\s+/g, "")}`;
    }
    if (key.includes("whatsapp")) {
      const num = digitsOnly(value);
      const text = encodeURIComponent(whatsappMessage);
      return `https://wa.me/${num}?text=${text}`;
    }
    return "#";
  };

  const getLinkProps = (label) => {
    const key = String(label || "").toLowerCase();
    if (key.includes("email")) {
      // return isMobileBP ? {} : { target: "_blank", rel: "noopener noreferrer" };
      return {};
    }
    if (key.includes("whatsapp")) {
      return { target: "_blank", rel: "noopener noreferrer" };
    }
    return {};
  };

  const handleEmailChoice = (choice) => {
    const email = selectedEmail;
    const gmailUrl = buildGmailComposeUrl(email, emailSubject, emailBody);
    const outlookUrl = buildOutlookComposeUrl(email, emailSubject, emailBody);
    const mailto = buildMailto(email, emailSubject, emailBody);

    setShowEmailPopup(false);

    if (choice === "gmail") {
      const w = window.open(gmailUrl, "_blank");

      setTimeout(() => {
        try {
          if (!w || w.closed) {
            window.location.href = mailto;
            return;
          }

          if (w.location?.href === "about:blank") {
            w.close();
            window.location.href = mailto;
          }
        } catch {}
      }, 3000);
    } else if (choice === "outlook") {
      const w = window.open(outlookUrl, "_blank");

      setTimeout(() => {
        try {
          if (!w || w.closed) {
            window.location.href = mailto;
            return;
          }

          if (w.location?.href === "about:blank") {
            w.close();
            window.location.href = mailto;
          }
        } catch {}
      }, 3000);
    }
  };

  const handleEmailClick = (e, email) => {
    e.preventDefault();
    setSelectedEmail(email);
    setShowEmailPopup(true);
  };

  return (
    <>
      <section
        className={[
          "relative flex flex-col mobile:flex-row items-center justify-between rounded-[20px]",
          "shadow-[inset_2px_4px_16px_rgba(248,248,248,0.06)] backdrop-blur-10 sm:backdrop-blur-[50px]",
          "gap-10 mobile:gap-0 sm:gap-0 md:gap-16 tablet:gap-20 lg:gap-[130px] 3xl:gap-[150px] 4xl:gap-[180px]",
          "px-4 sm:px-6 md:px-8 lg:px-11 3xl:px-14 4xl:px-20",
          "py-5 sm:py-6 md:py-8 lg:py-11 3xl:py-14 4xl:py-20",
          "mx-2 sm:mx-4 md:mx-6 lg:mx-8  xl:mx-[90px] laptop:mx-[100px] 2xl:mx-[100px] 3xl:mx-[120px] 4xl:mx-[140px] 4xl:min-h-[480px]",
          "w-full max-w-[1760px] min-h-80 sm:min-h-[350px] md:min-h-[380px] lg:min-h-[400px] 3xl:min-h-[440px] 4xl:min-h-[480px]",
          isDark ? "bg-[#FF1CF71C]/90" : "bg-[#FFFFFF]",
          className,
        ].join(" ")}
      >
        {/* Left content */}
        <div className="flex w-full lg:w-[65%] flex-col items-center gap-2 mobile:gap-4 sm:gap-6 md:gap-8 text-center lg:text-left">
          <h2
            className={[
              "text-center text-[22px] sm:text-[28px] md:text-[34px] lg:text-[35px] 3xl:text-[44px] 4xl:text-[52px]",
              "leading-7 sm:leading-9 md:leading-[42px] lg:leading-[49px] 3xl:leading-[54px] 4xl:leading-[62px]",
              "font-alexandria font-semibold uppercase",
              isDark
                ? "bg-[linear-gradient(94.97deg,var(--pinks)_46.31%,var(--secondary)_110.92%)] bg-clip-text text-transparent"
                : "text-[#1A1A1A]",
            ].join(" ")}
          >
            {heading}
          </h2>
          <p
            className={[
              "text-center text-sm sm:text-base md:text-[18px] lg:text-[20px] laptop:text-[22px] 3xl:text-[23px] 4xl:text-[24px]",
              "leading-5 sm:leading-[26px] md:leading-7 lg:leading-[30px] 3xl:leading-8 4xl:leading-[34px]",
              "font-manrope max-w-[90%] md:max-w-[600px] lg:max-w-[735px] 3xl:max-w-[820px] 4xl:max-w-[950px]",
              isDark ? "text-[#EEEEFF]" : "text-[#5A5A6E]",
            ].join(" ")}
          >
            {blurb}
          </p>

          <ButtonComponent
            className="mt-2 mobile:mt-3 sm:mt-6 px-3 lg:px-6 xl:px-6 3xl:px-7 4xl:px-8"
            label="Contact Us"
            onClick={handleScroll}
            hoverOverlay={true}
          />
        </div>

        {/* Right contact column */}
        <div className="flex flex-col mr-0 mobile:-mr-6 sm:mr-0 w-full max-w-[280px] sm:max-w-[300px] md:max-w-[320px] lg:w-[290px] gap-4 mobile:gap-4 sm:gap-10 md:gap-10 lg:gap-[63px] ">
          {contacts.map((c, i) => {
            const href = getContactHref(c.label, c.value);
            const linkProps = getLinkProps(c.label);
            return (
              <div
                key={i}
                className="flex items-center gap-12 mobile:gap-4 sm:gap-8 md:gap-16 lg:gap-20 justify-end lg:justify-start"
              >
                <IconBadgeImg src={c.icon} alt={`${c.label} icon`} />
                <div className="flex h-[62px] w-[150px] flex-col items-start gap-2">
                  <span
                    className={[
                      "font-alexandria font-medium",
                      "text-[16px] sm:text-[18px] md:text-[20px] lg:text-[22px]",
                      "leading-[22px] sm:leading-6 md:leading-[26px] lg:leading-[27px]",
                      isDark
                        ? "bg-[linear-gradient(95.73deg,#FF1CF7_45.56%,#EEEEFF_130.45%)] bg-clip-text text-transparent"
                        : "text-[#9C00BB]",
                    ].join(" ")}
                  >
                    {c.label}
                  </span>
                  <a
                    href={href}
                    {...linkProps}
                    onClick={(evt) => {
                      if ((c.label || "").toLowerCase().includes("email")) {
                        if (isMobileBP) return;
                        handleEmailClick(evt, c.value);
                      }
                    }}
                    className="font-manrope font-medium text-[14px] sm:text-[16px] md:text-[17px] lg:text-[18px] leading-5 sm:leading-[22px] md:leading-6 lg:leading-[25px] dark:text-[#FFFFFF] text-[#121212] hover:underline cursor-pointer break-all"
                    aria-label={`${c.label}: ${c.value}`}
                  >
                    {c.value}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <EmailPopupModal
        open={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        onSelect={handleEmailChoice}
      />
    </>
  );
}
